import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BookOpen,
  Beaker,
  MessageSquare,
  GraduationCap,
  Calendar,
  Briefcase,
  Users,
  FileText,
  Search,
  TrendingUp,
  Sparkles,
  CheckCircle2,
  Zap,
  Shield,
  Globe,
  ArrowRight,
  Star,
  Clock,
  Target,
  BarChart3,
  Award,
  Layers,
  Send,
  FolderOpen,
  UserPlus,
  Compass,
  Flame,
  Bell,
  Lock,
  Wallet,
  Play,
  Heart,
  Eye,
  Download,
  Hash,
  Mic,
  Video,
  Image,
  Paperclip,
} from 'lucide-react';
import LandingNavbar from './LandingNavbar';
import LandingFooter from './LandingFooter';
import './FeaturesPage.css';

/**
 * FeaturesPage — Professional features showcase
 * Layout: 9 alternating feature sections → Bottom CTA
 * Each section reveals on scroll via IntersectionObserver
 * Each visual card shows realistic, dynamic content
 */

const features = [
  {
    id: 'research-papers',
    label: 'Research Papers',
    tagColor: '#00C853',
    title: 'Discover papers that matter',
    description:
      'Access over 200 million research papers with AI-powered semantic search. Surface the most relevant work instantly, track trending research in your field, and get personalized recommendations based on your interests.',
    highlights: [
      { icon: Search, text: 'AI-powered semantic search across 200M+ papers' },
      { icon: TrendingUp, text: 'Real-time trending papers in your field' },
      { icon: Sparkles, text: 'Personalized recommendations powered by AI' },
      { icon: BookOpen, text: 'One-click save, cite, and share to your team' },
    ],
    visual: {
      gradient: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 50%, #A5D6A7 100%)',
      accent: '#00C853',
    },
    cardContent: {
      type: 'papers',
      tabs: ['Trending', 'Recommended', 'Saved'],
      activeTab: 0,
      items: [
        { title: 'Attention Is All You Need', authors: 'Vaswani et al.', year: '2017', citations: '98,421', tag: 'NLP' },
        { title: 'Deep Residual Learning for Image Recognition', authors: 'He, Zhang et al.', year: '2016', citations: '172,340', tag: 'CV' },
        { title: 'BERT: Pre-training of Deep Bidirectional Transformers', authors: 'Devlin et al.', year: '2019', citations: '65,882', tag: 'NLP' },
      ],
    },
  },
  {
    id: 'research-labs',
    label: 'Research Labs',
    tagColor: '#7C4DFF',
    title: 'Your team\'s private research HQ',
    description:
      'Spin up dedicated collaboration spaces in seconds. Each lab comes with built-in chat, task management, file storage, a shared research library, and a synced calendar — everything your team needs, unified.',
    highlights: [
      { icon: MessageSquare, text: 'Real-time team chat with threads and reactions' },
      { icon: CheckCircle2, text: 'Kanban task boards with assignees and deadlines' },
      { icon: FolderOpen, text: 'Organized file storage with version history' },
      { icon: Calendar, text: 'Shared calendar for meetings and milestones' },
    ],
    visual: {
      gradient: 'linear-gradient(135deg, #EDE7F6 0%, #D1C4E9 50%, #B39DDB 100%)',
      accent: '#7C4DFF',
    },
    cardContent: {
      type: 'labs',
      labName: 'NeuroCognition Lab',
      members: [
        { name: 'Dr. Sarah Chen', role: 'Lead', initials: 'SC', color: '#7C4DFF' },
        { name: 'James Wilson', role: 'Researcher', initials: 'JW', color: '#00BFA5' },
        { name: 'Emily Park', role: 'PhD Student', initials: 'EP', color: '#F50057' },
      ],
      tasks: [
        { text: 'Review fMRI protocol v3', done: true, assignee: 'SC' },
        { text: 'Submit IRB amendment', done: false, assignee: 'JW' },
        { text: 'Run EEG batch analysis', done: false, assignee: 'EP' },
      ],
    },
  },
  {
    id: 'communities',
    label: 'Communities',
    tagColor: '#FF6D00',
    title: 'Join your research tribe',
    description:
      'Find and join specialized research communities that match your interests. Participate in focused discussions, share breakthroughs, discover collaborators, and stay connected with the pulse of your field.',
    highlights: [
      { icon: Compass, text: 'Discover communities by field, topic, or institution' },
      { icon: Layers, text: 'Threaded discussions with rich media support' },
      { icon: Users, text: 'Member directories with expertise profiles' },
      { icon: BarChart3, text: 'Community analytics and engagement insights' },
    ],
    visual: {
      gradient: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 50%, #FFCC80 100%)',
      accent: '#FF6D00',
    },
    cardContent: {
      type: 'communities',
      items: [
        { name: 'Machine Learning', members: '14.2K', category: 'Computer Science', active: true },
        { name: 'Quantum Computing', members: '8.7K', category: 'Physics', active: false },
        { name: 'Genomics & CRISPR', members: '11.3K', category: 'Biology', active: true },
      ],
    },
  },
  {
    id: 'feed',
    label: 'Research Feed',
    tagColor: '#2979FF',
    title: 'Your personalized research stream',
    description:
      'A curated feed of posts, updates, and discussions from your network and communities. Share your progress, ask questions, run polls, and celebrate milestones — all in one living timeline.',
    highlights: [
      { icon: Flame, text: 'Trending topics and discussions in your field' },
      { icon: Star, text: 'Share publications, milestones, and research updates' },
      { icon: Target, text: 'Ask questions and run community polls' },
      { icon: Bell, text: 'Smart notifications for content that matters to you' },
    ],
    visual: {
      gradient: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 50%, #90CAF9 100%)',
      accent: '#2979FF',
    },
    cardContent: {
      type: 'feed',
      posts: [
        { author: 'Dr. Amira Hassan', avatar: 'AH', color: '#2979FF', text: 'Excited to share our new findings on transformer architectures for protein folding...', likes: 142, comments: 38, time: '2h ago' },
        { author: 'Prof. Li Wei', avatar: 'LW', color: '#00C853', text: 'Just published: "Zero-shot learning in low-resource languages" — link in bio!', likes: 89, comments: 21, time: '4h ago' },
      ],
    },
  },
  {
    id: 'collaborators',
    label: 'Collaborators',
    tagColor: '#00BFA5',
    title: 'Find the right people, fast',
    description:
      'Discover researchers who complement your work with AI-powered matching. Browse by field, institution, or expertise. Send connection requests, track your network, and build lasting research partnerships.',
    highlights: [
      { icon: Sparkles, text: 'AI-recommended collaborators based on your work' },
      { icon: UserPlus, text: 'One-click connection requests with intro messages' },
      { icon: Globe, text: 'Search by institution, field, or publication history' },
      { icon: Award, text: 'Mutual connections and shared interests highlighted' },
    ],
    visual: {
      gradient: 'linear-gradient(135deg, #E0F2F1 0%, #B2DFDB 50%, #80CBC4 100%)',
      accent: '#00BFA5',
    },
    cardContent: {
      type: 'collaborators',
      people: [
        { name: 'Dr. Yuki Tanaka', institution: 'MIT', field: 'Robotics', match: '94%', initials: 'YT', color: '#7C4DFF', mutuals: 12 },
        { name: 'Prof. Maya Singh', institution: 'Stanford', field: 'NLP', match: '91%', initials: 'MS', color: '#F50057', mutuals: 8 },
        { name: 'Dr. Alex Rivera', institution: 'Oxford', field: 'ML Theory', match: '87%', initials: 'AR', color: '#FF6D00', mutuals: 5 },
      ],
    },
  },
  {
    id: 'courses',
    label: 'Courses',
    tagColor: '#F50057',
    title: 'Learn from the experts',
    description:
      'Access expert-led courses designed for researchers. From methodology deep-dives to cutting-edge techniques, each course includes structured curriculum, hands-on assignments, discussion forums, and verified certificates.',
    highlights: [
      { icon: Play, text: 'Video lectures with interactive code playgrounds' },
      { icon: Target, text: 'Structured curriculum with progress tracking' },
      { icon: MessageSquare, text: 'Course discussion forums and Q&A' },
      { icon: Award, text: 'Verified completion certificates' },
    ],
    visual: {
      gradient: 'linear-gradient(135deg, #FCE4EC 0%, #F8BBD0 50%, #F48FB1 100%)',
      accent: '#F50057',
    },
    cardContent: {
      type: 'courses',
      items: [
        { title: 'Advanced Statistical Methods', instructor: 'Prof. Maria Torres', progress: 72, lessons: '24 lessons', tag: 'Statistics' },
        { title: 'Deep Learning for Scientists', instructor: 'Dr. James Lee', progress: 45, lessons: '18 lessons', tag: 'AI/ML' },
        { title: 'Scientific Writing Mastery', instructor: 'Prof. Anna Reid', progress: 100, lessons: '12 lessons', tag: 'Writing' },
      ],
    },
  },
  {
    id: 'workshops',
    label: 'Workshops',
    tagColor: '#AA00FF',
    title: 'Live sessions that level you up',
    description:
      'Attend live, interactive workshops led by domain experts. Get hands-on experience, real-time feedback, and access to exclusive resources. Never miss a session with calendar sync and recording access.',
    highlights: [
      { icon: Calendar, text: 'Schedule with calendar sync and reminders' },
      { icon: Users, text: 'Interactive sessions with live Q&A' },
      { icon: FolderOpen, text: 'Downloadable resources and materials' },
      { icon: Clock, text: 'Session recordings available post-event' },
    ],
    visual: {
      gradient: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 50%, #CE93D8 100%)',
      accent: '#AA00FF',
    },
    cardContent: {
      type: 'workshops',
      items: [
        { title: 'Intro to Bayesian Inference', date: 'Feb 18, 2026', time: '2:00 PM EST', speaker: 'Dr. Nina Patel', attendees: 234, status: 'live' },
        { title: 'Research Data Visualization', date: 'Feb 22, 2026', time: '10:00 AM EST', speaker: 'Prof. Erik Holm', attendees: 156, status: 'upcoming' },
        { title: 'Grant Writing Workshop', date: 'Feb 14, 2026', time: '3:00 PM EST', speaker: 'Dr. Rosa Kim', attendees: 312, status: 'recorded' },
      ],
    },
  },
  {
    id: 'freelance',
    label: 'Freelance',
    tagColor: '#FF6D00',
    title: 'Monetize your expertise',
    description:
      'Browse and post research tasks on our built-in freelance marketplace. Find specialists for data analysis, peer review, literature surveys, and more — or offer your own skills to the research community.',
    highlights: [
      { icon: Search, text: 'Browse tasks by field, budget, and deadline' },
      { icon: Shield, text: 'Secure milestone-based payments' },
      { icon: Star, text: 'Reputation system with verified reviews' },
      { icon: Wallet, text: 'Earnings dashboard with payout tracking' },
    ],
    visual: {
      gradient: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 50%, #FFE082 100%)',
      accent: '#FF6D00',
    },
    cardContent: {
      type: 'freelance',
      tasks: [
        { title: 'Statistical Analysis — Clinical Trial Data', budget: '$850', deadline: '5 days', bids: 12, field: 'Biostatistics' },
        { title: 'Literature Review — Quantum Error Correction', budget: '$400', deadline: '10 days', bids: 7, field: 'Physics' },
        { title: 'Data Visualization — Genomic Datasets', budget: '$600', deadline: '7 days', bids: 9, field: 'Bioinformatics' },
      ],
    },
  },
  {
    id: 'messaging',
    label: 'Messaging',
    tagColor: '#0091EA',
    title: 'Conversations that move research forward',
    description:
      'Real-time messaging built for researchers. Rich text, file sharing, code snippets, and threaded replies — all in a clean interface. Star important conversations, pin messages, and never lose context.',
    highlights: [
      { icon: Send, text: 'Real-time messaging with typing indicators' },
      { icon: Lock, text: 'End-to-end encrypted private conversations' },
      { icon: FileText, text: 'Share files, papers, and code snippets inline' },
      { icon: Star, text: 'Star, pin, and archive for fast retrieval' },
    ],
    visual: {
      gradient: 'linear-gradient(135deg, #E1F5FE 0%, #B3E5FC 50%, #81D4FA 100%)',
      accent: '#0091EA',
    },
    cardContent: {
      type: 'messaging',
      conversations: [
        { name: 'NeuroCog Lab', lastMessage: 'Emily: The fMRI results are ready for review!', time: '2m', unread: 3, isGroup: true },
        { name: 'Dr. Sarah Chen', lastMessage: 'Thanks for the manuscript feedback — I\'ll revise section 3', time: '15m', unread: 0, isGroup: false },
        { name: 'ML Reading Group', lastMessage: 'James: Has anyone read the new Mamba paper?', time: '1h', unread: 8, isGroup: true },
      ],
    },
  },
];

/* ─────────────────────────────────────────────────────────
   VISUAL CARD RENDERERS — One per card type
   ───────────────────────────────────────────────────────── */

const renderPapersCard = (data, accent) => (
  <>
    <div className="fp-card-tabs">
      {data.tabs.map((tab, i) => (
        <span key={i} className={`fp-card-tab ${i === data.activeTab ? 'active' : ''}`} style={{ '--tab-color': accent }}>{tab}</span>
      ))}
    </div>
    <div className="fp-card-list">
      {data.items.map((paper, i) => (
        <div key={i} className="fp-card-paper">
          <div className="fp-card-paper-icon" style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)`, color: accent }}>
            <FileText size={16} />
          </div>
          <div className="fp-card-paper-info">
            <span className="fp-card-paper-title">{paper.title}</span>
            <span className="fp-card-paper-meta">{paper.authors} · {paper.year}</span>
          </div>
          <div className="fp-card-paper-right">
            <span className="fp-card-tag" style={{ background: `color-mix(in srgb, ${accent} 10%, transparent)`, color: accent }}>{paper.tag}</span>
            <span className="fp-card-paper-cites">{paper.citations} cites</span>
          </div>
        </div>
      ))}
    </div>
  </>
);

const renderLabsCard = (data, accent) => (
  <>
    <div className="fp-card-lab-header">
      <div className="fp-card-lab-icon" style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)`, color: accent }}>
        <Beaker size={18} />
      </div>
      <div>
        <span className="fp-card-lab-name">{data.labName}</span>
        <span className="fp-card-lab-count">{data.members.length} members</span>
      </div>
    </div>
    <div className="fp-card-members">
      {data.members.map((m, i) => (
        <div key={i} className="fp-card-member">
          <div className="fp-card-avatar" style={{ background: m.color }}>{m.initials}</div>
          <span className="fp-card-member-name">{m.name}</span>
          <span className="fp-card-member-role">{m.role}</span>
        </div>
      ))}
    </div>
    <div className="fp-card-tasks">
      {data.tasks.map((t, i) => (
        <div key={i} className="fp-card-task">
          <div className={`fp-card-checkbox ${t.done ? 'checked' : ''}`} style={{ '--check-color': accent }}>
            {t.done && <CheckCircle2 size={14} />}
          </div>
          <span className={`fp-card-task-text ${t.done ? 'done' : ''}`}>{t.text}</span>
          <span className="fp-card-task-assignee">{t.assignee}</span>
        </div>
      ))}
    </div>
  </>
);

const renderCommunitiesCard = (data, accent) => (
  <div className="fp-card-communities">
    {data.items.map((c, i) => (
      <div key={i} className="fp-card-community">
        <div className="fp-card-community-left">
          <div className="fp-card-community-avatar" style={{ background: `color-mix(in srgb, ${accent} 15%, transparent)`, color: accent }}>
            <Hash size={16} />
          </div>
          <div className="fp-card-community-info">
            <span className="fp-card-community-name">{c.name}</span>
            <span className="fp-card-community-meta">{c.members} members · {c.category}</span>
          </div>
        </div>
        <button className={`fp-card-community-btn ${c.active ? 'joined' : ''}`} style={{ '--btn-color': accent }}>
          {c.active ? 'Joined' : 'Join'}
        </button>
      </div>
    ))}
  </div>
);

const renderFeedCard = (data, accent) => (
  <div className="fp-card-feed">
    {data.posts.map((post, i) => (
      <div key={i} className="fp-card-post">
        <div className="fp-card-post-header">
          <div className="fp-card-avatar" style={{ background: post.color }}>{post.avatar}</div>
          <div className="fp-card-post-author-info">
            <span className="fp-card-post-author">{post.author}</span>
            <span className="fp-card-post-time">{post.time}</span>
          </div>
        </div>
        <p className="fp-card-post-text">{post.text}</p>
        <div className="fp-card-post-actions">
          <span className="fp-card-post-action"><Heart size={13} /> {post.likes}</span>
          <span className="fp-card-post-action"><MessageSquare size={13} /> {post.comments}</span>
          <span className="fp-card-post-action"><Send size={13} /> Share</span>
        </div>
      </div>
    ))}
  </div>
);

const renderCollaboratorsCard = (data, accent) => (
  <div className="fp-card-collabs">
    {data.people.map((p, i) => (
      <div key={i} className="fp-card-collab">
        <div className="fp-card-collab-left">
          <div className="fp-card-avatar" style={{ background: p.color }}>{p.initials}</div>
          <div className="fp-card-collab-info">
            <span className="fp-card-collab-name">{p.name}</span>
            <span className="fp-card-collab-meta">{p.institution} · {p.field}</span>
            <span className="fp-card-collab-mutuals">{p.mutuals} mutual connections</span>
          </div>
        </div>
        <div className="fp-card-collab-right">
          <span className="fp-card-match" style={{ color: accent }}>{p.match}</span>
          <button className="fp-card-connect-btn" style={{ '--btn-color': accent }}>
            <UserPlus size={13} /> Connect
          </button>
        </div>
      </div>
    ))}
  </div>
);

const renderCoursesCard = (data, accent) => (
  <div className="fp-card-courses">
    {data.items.map((course, i) => (
      <div key={i} className="fp-card-course">
        <div className="fp-card-course-top">
          <div className="fp-card-course-icon" style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)`, color: accent }}>
            <Play size={14} />
          </div>
          <span className="fp-card-tag" style={{ background: `color-mix(in srgb, ${accent} 10%, transparent)`, color: accent }}>{course.tag}</span>
        </div>
        <span className="fp-card-course-title">{course.title}</span>
        <span className="fp-card-course-instructor">{course.instructor} · {course.lessons}</span>
        <div className="fp-card-progress-bar">
          <div className="fp-card-progress-fill" style={{ width: `${course.progress}%`, background: accent }} />
        </div>
        <span className="fp-card-progress-label" style={{ color: accent }}>{course.progress}% complete</span>
      </div>
    ))}
  </div>
);

const renderWorkshopsCard = (data, accent) => (
  <div className="fp-card-workshops">
    {data.items.map((ws, i) => (
      <div key={i} className="fp-card-workshop">
        <div className="fp-card-workshop-status">
          <span className={`fp-card-status-dot ${ws.status}`} />
          <span className={`fp-card-status-text ${ws.status}`}>
            {ws.status === 'live' ? 'Live Now' : ws.status === 'upcoming' ? 'Upcoming' : 'Recorded'}
          </span>
        </div>
        <span className="fp-card-workshop-title">{ws.title}</span>
        <span className="fp-card-workshop-meta">
          <Calendar size={12} /> {ws.date} · {ws.time}
        </span>
        <div className="fp-card-workshop-footer">
          <span className="fp-card-workshop-speaker">{ws.speaker}</span>
          <span className="fp-card-workshop-attendees"><Users size={12} /> {ws.attendees}</span>
        </div>
      </div>
    ))}
  </div>
);

const renderFreelanceCard = (data, accent) => (
  <div className="fp-card-freelance">
    {data.tasks.map((task, i) => (
      <div key={i} className="fp-card-ftask">
        <div className="fp-card-ftask-top">
          <span className="fp-card-tag" style={{ background: `color-mix(in srgb, ${accent} 10%, transparent)`, color: accent }}>{task.field}</span>
          <span className="fp-card-ftask-budget">{task.budget}</span>
        </div>
        <span className="fp-card-ftask-title">{task.title}</span>
        <div className="fp-card-ftask-footer">
          <span className="fp-card-ftask-meta"><Clock size={12} /> {task.deadline}</span>
          <span className="fp-card-ftask-meta"><Users size={12} /> {task.bids} bids</span>
        </div>
      </div>
    ))}
  </div>
);

const renderMessagingCard = (data, accent) => (
  <div className="fp-card-messaging">
    {data.conversations.map((conv, i) => (
      <div key={i} className="fp-card-conversation">
        <div className="fp-card-conv-avatar" style={{ background: `color-mix(in srgb, ${accent} 15%, transparent)`, color: accent }}>
          {conv.isGroup ? <Users size={16} /> : conv.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
        </div>
        <div className="fp-card-conv-info">
          <div className="fp-card-conv-top">
            <span className="fp-card-conv-name">{conv.name}</span>
            <span className="fp-card-conv-time">{conv.time}</span>
          </div>
          <span className="fp-card-conv-message">{conv.lastMessage}</span>
        </div>
        {conv.unread > 0 && (
          <span className="fp-card-conv-badge" style={{ background: accent }}>{conv.unread}</span>
        )}
      </div>
    ))}
  </div>
);

const cardRenderers = {
  papers: renderPapersCard,
  labs: renderLabsCard,
  communities: renderCommunitiesCard,
  feed: renderFeedCard,
  collaborators: renderCollaboratorsCard,
  courses: renderCoursesCard,
  workshops: renderWorkshopsCard,
  freelance: renderFreelanceCard,
  messaging: renderMessagingCard,
};

/* ─────────────────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────────────────── */

const featureIcons = {
  'research-papers': BookOpen,
  'research-labs': Beaker,
  'communities': Users,
  'feed': FileText,
  'collaborators': UserPlus,
  'courses': GraduationCap,
  'workshops': Calendar,
  'freelance': Briefcase,
  'messaging': Send,
};

const FeaturesPage = () => {
  const location = useLocation();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [activeFeature, setActiveFeature] = useState(features[0].id);

  // Scroll to hash anchor on mount or hash change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const navHeight = 80;
          const y = el.getBoundingClientRect().top + window.scrollY - navHeight;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  // IntersectionObserver for scroll-triggered animations + active feature tracking
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
            // Update active feature in nav
            if (entry.target.classList.contains('fp-section')) {
              setActiveFeature(entry.target.id);
            }
          }
        });
      },
      { threshold: 0.15, rootMargin: '-80px 0px -40% 0px' }
    );

    const sections = document.querySelectorAll('.fp-section');
    sections.forEach((section) => observer.observe(section));

    const heroEl = document.querySelector('.fp-hero');
    if (heroEl) observer.observe(heroEl);

    const cta = document.querySelector('.fp-bottom-cta');
    if (cta) observer.observe(cta);

    return () => observer.disconnect();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  const scrollToFeature = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const navHeight = 80;
      const y = el.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="fp-page">
      <LandingNavbar />

      {/* ── Hero Section ── */}
      <section id="fp-hero" className={`fp-hero ${isVisible('fp-hero') ? 'fp-visible' : ''}`}>
        <div className="fp-hero-inner">
          <h1 className="fp-hero-title">
            Everything you need to<br />
            <span className="fp-hero-gradient">accelerate your research</span>
          </h1>
          <p className="fp-hero-subtitle">
            Nine powerful tools, one unified platform. From discovering papers to
            finding collaborators, ResearchPro gives your team the edge.
          </p>
        </div>
      </section>

      {/* ── Sticky Feature Navigation ── */}
      <div className="fp-feature-nav-wrapper">
        <nav className="fp-feature-nav">
          <div className="fp-feature-nav-inner">
            {features.map((f) => {
              const Icon = featureIcons[f.id];
              return (
                <button
                  key={f.id}
                  className={`fp-feature-nav-item ${activeFeature === f.id ? 'active' : ''}`}
                  onClick={() => scrollToFeature(f.id)}
                  style={{ '--nav-color': f.tagColor }}
                >
                  <Icon size={14} />
                  <span>{f.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* ── Feature Sections ── */}
      {features.map((feature, index) => {
        const isEven = index % 2 === 1;
        const renderer = cardRenderers[feature.cardContent.type];

        return (
          <section
            key={feature.id}
            id={feature.id}
            className={`fp-section ${isEven ? 'fp-section--reversed' : ''} ${
              isVisible(feature.id) ? 'fp-visible' : ''
            }`}
          >
            <div className="fp-section-inner">
              {/* Text Content */}
              <div className="fp-section-text">
                <div className="fp-section-label-row">
                  <span className="fp-section-number" style={{ '--label-color': feature.tagColor }}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span
                    className="fp-section-label"
                    style={{ '--label-color': feature.tagColor }}
                  >
                    {feature.label}
                  </span>
                </div>
                <h2 className="fp-section-title">{feature.title}</h2>
                <p className="fp-section-desc">{feature.description}</p>

                <ul className="fp-section-highlights">
                  {feature.highlights.map((h, i) => (
                    <li key={i} className="fp-highlight" style={{ '--delay': `${i * 80}ms` }}>
                      <div
                        className="fp-highlight-icon"
                        style={{ '--hi-color': feature.tagColor }}
                      >
                        <h.icon size={15} />
                      </div>
                      <span>{h.text}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/signup"
                  className="fp-section-cta"
                  style={{ '--cta-color': feature.tagColor }}
                >
                  Get started with {feature.label}
                  <ArrowRight size={15} />
                </Link>
              </div>

              {/* Visual Card */}
              <div className="fp-section-visual">
                <div
                  className="fp-visual-card"
                  style={{
                    '--card-accent': feature.visual.accent,
                  }}
                >
                  <div className="fp-visual-card-inner">
                    {/* Top bar */}
                    <div className="fp-visual-topbar">
                      <div className="fp-visual-dots">
                        <span /><span /><span />
                      </div>
                      <span className="fp-visual-topbar-title">{feature.label}</span>
                    </div>

                    {/* Dynamic content */}
                    <div className="fp-visual-content">
                      {renderer(feature.cardContent, feature.visual.accent)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ── Bottom CTA ── */}
      <section
        id="fp-bottom-cta"
        className={`fp-bottom-cta ${isVisible('fp-bottom-cta') ? 'fp-visible' : ''}`}
      >
        <div className="fp-cta-inner">
          <span className="fp-cta-badge">
            <Zap size={13} />
            Get Started
          </span>
          <h2 className="fp-cta-title">Ready to transform your research?</h2>
          <p className="fp-cta-subtitle">
            Join thousands of researchers who are already working smarter, faster, and more
            collaboratively on ResearchPro.
          </p>
          <div className="fp-cta-actions">
            <Link to="/signup" className="fp-btn fp-btn--primary fp-btn--large">
              Get Started — It's Free
              <ArrowRight size={18} />
            </Link>
            <Link to="/" className="fp-btn fp-btn--secondary fp-btn--large">
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default FeaturesPage;
